import React, {
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { flowRight } from 'lodash';
import FocusTrap from 'focus-trap-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService,
} from '@els/els-component-toast-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService,
} from '@els/els-component-modal-react';
import { ELSIcon } from '@els/els-component-form-field-react';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { FlexItem } from '../flex/FlexItem.component';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import PanelLeftIcon from '../panel-left-icon/PanelLeftIcon.component';
import {
  getBrand,
  getBrandName,
  getChatContextsForRequest,
  getChatUserRoleFromTokenRole,
  getIsDirectAccessByFeatureFlag,
  getOrderedPurchasedEntitlements,
  getOsmosisAppLinkContext,
  getSortedChats,
  getUserName,
  isBankBasedCourse,
  isIframeContext,
  isInputBlocked,
  isShowPerformanceNav,
  isVSTChat,
  toastActiveStreamingError
} from '../../pages/ai-chat/ai-chat.utilities';
import { getParentLinkId, preserveAdminOverrideParam } from '../../utilities/app.utilities';
import {
  fetchChatEntitlementsAction,
  fetchChatsAction,
  fetchEolsUserAction,
  fetchOsmosisTokenAction,
  handleChatClickAction,
  returnAppLinkAction,
  trackNewRelicAction
} from '../app/app-state-actions.utilities';
import { AppContext } from '../app/AppProvider.component';
import { getTokenIsbnsArray } from '../app/app-state.utilities';
import cx from 'classnames';
import {
  ELSButton,
  ELSTooltip
} from '../els.components';
import IconWithText from '../icon-with-text/IconWithText.component';
import {
  ChatEntitlementDto,
  ChatEntitlementTypeDto,
  ChatUserRoleDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { useHistory } from 'react-router';
import { RoutePath } from '../app/app.constants';
import withPageLoader from '../../hocs/with-page-loader/withPageLoader.hoc';
import LoadingEllipsis from '../loading-ellipsis/LoadingEllipsis.component';
import {
  mobileBreakpoint,
  SidebarItem,
  SidebarItemConfig,
  sidebarWidthClosed,
  sidebarWidthOpen
} from './ai-chat-base-template.constants';
import { ELSPageLoader } from '@els/els-ui-common-react';
import ChatActionMenu from '../chat-action-menu/ChatActionMenu.component';
import {
  ChatModeType,
  ChatUserRoleDisplayMap,
  IframeEvent
} from '../../pages/ai-chat/ai-chat.constants';
import IsRender from '../is-render/IsRender.component';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { isInstructor } from '../../utilities/common.utilities';
import { Brand } from '../../constants/common.constants';

export type AiChatBaseTemplatePropsOnly = {
  children?: ReactNode;
  panelContent: ReactNode;
  isPanelOpen: boolean;
};

export type AiChatBaseTemplateProps = ELSPropsFromToastService
  & ELSPropsFromModalService
  & AiChatBaseTemplatePropsOnly;

export type AiChatBaseTemplateState = {
  sidebarWidth: number;
  hideTooltip: boolean;
  isMobile: boolean;
}

export const AiChatBaseTemplateComponent = (props: AiChatBaseTemplateProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const history = useHistory();

  const [state, setState] = useState<AiChatBaseTemplateState>(() => {
    const isMobile = window.innerWidth <= mobileBreakpoint;
    return {
      sidebarWidth: isMobile ? sidebarWidthOpen : (appState.isSidebarOpen ? sidebarWidthOpen : sidebarWidthClosed),
      hideTooltip: true,
      isMobile,
    };
  });

  useEffect(() => {

    fetchOsmosisTokenAction({ appState, appStateRef, setAppState });
    fetchEolsUserAction({ appState, appStateRef, setAppState, userId: appState.userId, useCache: true });

    if (appState.entitlements && appState.chats) {
      return;
    }

    fetchChatEntitlementsAction({ appState, appStateRef, setAppState, useCache: true, isOsmosis: brand === Brand.OSMOSIS_AI })
      .then((entitlements: Partial<Record<ChatEntitlementTypeDto, ChatEntitlementDto[]>>) => {

        fetchChatsAction({
          appState,
          appStateRef,
          setAppState,
          contextType: null,
          contextId: null,
          page: null,
          pageSize: null,
          useCache: true,
          userRole: appState.chatUserRole,
          context: getChatContextsForRequest({
            ...appState,
            entitlements
          })
        }).then((chats) => {

          setAppState((prevState) => ({
            ...prevState,
            chats,
            entitlements,
            chatUserRole: !prevState.chatUserRole ? getChatUserRoleFromTokenRole(prevState.userRole) : prevState.chatUserRole
          }));
        });
      })
  }, []);

  useEffect(() => {
    if (appState.chatUserRole !== ChatUserRoleDto.INSTRUCTOR && history.location.pathname.includes(RoutePath.AI_CHAT_PERFORMANCE)) {
      history.replace(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
    }
  }, [history.location.pathname, appState.chatUserRole]);

  const toggleSidebarWidth = (targetStateIsOpen: boolean, currentWidth: number) => {
    // If mobile, always set width to 300 (open)
    const isMobile = window.innerWidth <= mobileBreakpoint;
    if (isMobile) {
      setState((prevState) => ({
        ...prevState,
        sidebarWidth: sidebarWidthOpen
      }));
      return;
    }

    const targetWidth = targetStateIsOpen ? sidebarWidthOpen : sidebarWidthClosed;

    if (targetWidth === currentWidth) {
      return;
    }
    const adder = targetWidth > currentWidth ? 20 : -20;
    const newWidth = currentWidth + adder;
    setState((prevState) => ({
      ...prevState,
      sidebarWidth: newWidth
    }));
    setTimeout(() => {
      toggleSidebarWidth(targetStateIsOpen, newWidth);
    }, 11);
  }

  useEffect(() => {
    toggleSidebarWidth(appState.isSidebarOpen, state.sidebarWidth)
  }, [appState.isSidebarOpen]);

  useEffect(() => {
    if (props.isPanelOpen && window.innerWidth < 1200) {
      setAppState((prevState) => {
        return {
          ...prevState,
          isSidebarOpen: false,
        }
      });
    }
  }, [props.isPanelOpen]);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= mobileBreakpoint;

      // Update mobile state
      setState((prevState) => {
        const updates: Partial<AiChatBaseTemplateState> = {};

        if (prevState.isMobile !== isMobile) {
          updates.isMobile = isMobile;
        }

        // Always set width to 300 (open) when mobile
        if (isMobile) {
          updates.sidebarWidth = sidebarWidthOpen;
        } else {
          // When transitioning from mobile to desktop, set appropriate width
          const desiredWidth = appState.isSidebarOpen ? sidebarWidthOpen : sidebarWidthClosed;
          if (prevState.sidebarWidth !== desiredWidth) {
            updates.sidebarWidth = desiredWidth;
          }
        }

        return Object.keys(updates).length > 0 ? { ...prevState, ...updates } : prevState;
      });

      // Close sidebar if panel is open and screen is small
      if (props.isPanelOpen && window.innerWidth < 1200) {
        setAppState((prevState) => {
          return {
            ...prevState,
            isSidebarOpen: false,
          }
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [props.panelContent, appState.isSidebarOpen]);

  const handleReturnClick = () => {
    // Close sidebar on mobile after clicking
    if (state.isMobile) {
      setAppState((prevState) => ({
        ...prevState,
        isSidebarOpen: false,
      }));
    }

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_RETURN_CLICK,
        props: null
      }
    });

    if (getBrand(appState) === Brand.OSMOSIS_AI) {
      window.location.href = getOsmosisAppLinkContext(appState.appLinkData).linkBackUrl || 'https://www.osmosis.org/';
      return;
    }

    returnAppLinkAction({
      appState,
      setAppState,
      appStateRef,
      linkId: getParentLinkId(appState.appLinkData, appState.appLinkCookies),
      returnPostBody: null
    });
  };

  const handlePerformanceClick = () => {

    const targetPage = RoutePath.AI_CHAT_PERFORMANCE;

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_TOP_NAV_CLICK,
        props: {
          targetPage
        }
      }
    });

    history.push(targetPage);
  };

  const handleNewChatClick = () => {

    const { isLoading } = appState;

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_NEW_CHAT_CLICK,
        props: {
          isStreaming: isLoading,
          chatUserRole: appState.chatUserRole,
        }
      }
    });

    // No chat row is created here -- it is created (with its real title) when the user submits
    // their first message. See getActiveOrCreateChat in AiChat.page.tsx.
    setAppState((prevState) => {
      return {
        ...prevState,
        activeChatId: null,
        showLoader: false,
      }
    })

    history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
  };

  const handleYourBooksClick = () => {
    history.push(RoutePath.YOUR_BOOKS);
  };

  const handlePreviewChatsClick = () => {
    history.push(RoutePath.YOUR_CHATS);
  };

  const handleHowItWorksClick = () => {
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.AI_CHAT_HOW_IT_WORKS_LINK_CLICK,
        props: null
      }
    });

    history.push(RoutePath.HOW_IT_WORKS);
  };

  const handleInstructorRoleToggle = () => {
    if (!isInstructor(appState.userRole)) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      hideTooltip: true,
    }));
    setAppState((prevState) => ({
      ...prevState,
      showLoader: true
    }));

    const chatUserRoleToUpdate = appState.chatUserRole === ChatUserRoleDto.INSTRUCTOR_AS_STUDENT
      ? ChatUserRoleDto.INSTRUCTOR
      : ChatUserRoleDto.INSTRUCTOR_AS_STUDENT;

    fetchChatsAction({
      appState,
      appStateRef,
      setAppState,
      contextType: null,
      contextId: null,
      page: null,
      pageSize: null,
      useCache: false,
      userRole: chatUserRoleToUpdate,
      context: getChatContextsForRequest(appState)
    }).then((chats) => {
      setAppState((prevState) => ({
        ...prevState,
        chats,
        chatUserRole: chatUserRoleToUpdate,
        chatModeType: ChatModeType.DEFAULT_STUDY_MODE,
      }));

      if (!chats || !chats.length) {
        // No chat row is created here -- it is created (with its real title) when the user
        // submits their first message. See getActiveOrCreateChat in AiChat.page.tsx.
        setAppState((prevState) => ({
          ...prevState,
          activeChatId: null,
          showLoader: false
        }));

        history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
        return;
      }

      const [latestChat] = getSortedChats(chats);

      setAppState((prevState) => ({
        ...prevState,
        activeChatId: latestChat.id,
      }));
      history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search));
    }).catch(() => {
      setAppState((prevState) => ({
        ...prevState,
        showLoader: false
      }));
    });
  };

  const handleSidebarItemClick = (item: SidebarItem) => {

    const {
      toastService,
    } = props;

    if (isInputBlocked(appState)) {
      toastActiveStreamingError({
        appState,
        appStateRef,
        setAppState,
        toastService,
        trackNewRelicAction,
        actionRef: item
      });
      return;
    }

    // Close sidebar on mobile after clicking
    if (state.isMobile) {
      setAppState((prevState) => ({
        ...prevState,
        isSidebarOpen: false,
      }));
    }

    switch (item) {
      case SidebarItem.NEW_CHAT:
        handleNewChatClick();
        break;
      case SidebarItem.YOUR_CHATS:
        handlePreviewChatsClick();
        break;
      case SidebarItem.YOUR_BOOKS:
        handleYourBooksClick();
        break;
      case SidebarItem.INSTRUCTOR_INSIGHTS:
        handlePerformanceClick();
        break;
      case SidebarItem.HOW_IT_WORKS:
        handleHowItWorksClick();
        break;
      case SidebarItem.VIEW_AS_STUDENT:
      case SidebarItem.BACK_TO_INSTRUCTOR_VIEW:
        handleInstructorRoleToggle();
        break;
      default:
        break;
    }
  }

  const isDirectAccessByFeatureFlag = getIsDirectAccessByFeatureFlag(
    appState.featureFlagsGrouped,
    appState.courseSectionId,
    getTokenIsbnsArray()
  );
  const brand = getBrand(appState);
  const isIframe = isIframeContext(appState.appLinkData)
  const hideNav = appState.appLinkData?.outPostBody?.hideNav
  const disableNewChat = appState.appLinkData?.outPostBody?.disableNewChat
  const disabledMyBooks = appState.appLinkData?.outPostBody?.disabledMyBooks
  const enableHeaderLogoIframeLink = appState.appLinkData?.outPostBody?.enableHeaderLogoIframeLink ?? true
  // Osmosis returns via linkBackUrl and has a hardcoded fallback, so it needs no parent link.
  // Every other brand posts to /api/app-link/return/{linkId}, so without a parent link there is
  // nowhere to return to and the button would only produce a 400.
  // AI Coach (Spark) has no parent link to return to and no brand-specific link-back URL, so
  // the return control is never rendered rather than posting a return that would only 400.
  const canReturn = brand !== Brand.AI_COACH
    && (brand === Brand.OSMOSIS_AI
      || Boolean(getParentLinkId(appState.appLinkData, appState.appLinkCookies)));
  const enableReturn = !isIframe
    && !isDirectAccessByFeatureFlag
    && !isBankBasedCourse(appState.entitlements)
    && !isVSTChat(appState)
    && canReturn;
  const showHeaderLogoAsLink = isIframe && enableHeaderLogoIframeLink;

  const getSidebarItems = (): SidebarItem[] => {
    const items = [
      SidebarItem.NEW_CHAT,
      SidebarItem.YOUR_CHATS,
    ]

    const entitlements = getOrderedPurchasedEntitlements(appState.entitlements);

    if (
      brand === Brand.SHERPATH_AI
      && !isDirectAccessByFeatureFlag
      && entitlements?.length
      && !disabledMyBooks
    ) {
      items.push(SidebarItem.YOUR_BOOKS);
    }

    if (isShowPerformanceNav(appState) && appState.chatUserRole === ChatUserRoleDto.INSTRUCTOR) {
      items.push(SidebarItem.INSTRUCTOR_INSIGHTS);
    }

    return disableNewChat ? items.filter(item => item !== SidebarItem.NEW_CHAT) : items;

  }

  const toggleSidebarButton = (
    <button
      className="u-els-debuttonize u-els-display-block"
      title={appState.isSidebarOpen ? 'toggle sidebar closed' : 'toggle sidebar open'}
      type="button"
      onClick={() => {

        setAppState((prevState) => {
          return {
            ...prevState,
            isSidebarOpen: !prevState.isSidebarOpen,
          }
        })

      }}
    >
      <PanelLeftIcon size="1x1o2" />
      <span className="u-els-hide-visually">{appState.isSidebarOpen ? 'toggle sidebar closed' : 'toggle sidebar open'}</span>
    </button>
  );

  const getChatUserRoleTooltipContent = () => {
    if (!appState?.chatUserRole || !isInstructor(appState.userRole)) {
      return null;
    }

    const sidebarItemToShow = appState.chatUserRole === ChatUserRoleDto.INSTRUCTOR
      ? SidebarItem.VIEW_AS_STUDENT
      : SidebarItem.BACK_TO_INSTRUCTOR_VIEW;

    const config = SidebarItemConfig[sidebarItemToShow];
    if (!config) {
      return null;
    }

    return (
      <div>
        <button
          type="button"
          title={config.text}
          className="u-els-anchorize"
          onClick={() => handleSidebarItemClick(sidebarItemToShow)}
        >
          <IconWithText
            iconName={config.iconName}
            iconPrefix={config.iconPrefix}
            iconSize="1x1o4"
          >
            {config.text}
          </IconWithText>
        </button>
      </div>
    );
  };

  return (
    <>
      {
        !appState.entitlements && (
          <div className="c-els-modal">
            <div className="c-els-modal__window">
              <LoadingEllipsis text="Loading your resources" />
            </div>
          </div>
        )
      }

      {
        appState.showLoader && (
          <ELSPageLoader />
        )
      }

      <div className="c-naa-ai-chat-base-wrapper">
        <IsRender isRender={appState.chatUserRole === ChatUserRoleDto.INSTRUCTOR_AS_STUDENT}>
          <div className="c-naa-ai-chat-base__instructor-header">
            <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.LEFT]}>
              <FlexItem>
                <span className="u-els-font-size-h4">You are viewing {getBrandName(appState)} as a student</span>
              </FlexItem>
              <FlexItem>
                <ELSButton
                  size={ELSButtonSize.SMALL}
                  type={ELSButtonType.SECONDARY}
                  onClick={handleInstructorRoleToggle}
                >
                  {SidebarItemConfig[SidebarItem.BACK_TO_INSTRUCTOR_VIEW].text}
                </ELSButton>
              </FlexItem>
            </FlexLayout>
          </div>
        </IsRender>

        <div
          className={cx("c-naa-ai-chat-base", {
            'c-naa-ai-chat-base--sidebar-open': !hideNav && appState.isSidebarOpen,
            'c-naa-ai-chat-base--sidebar-closed': !hideNav && !appState.isSidebarOpen,
            'c-naa-ai-chat-base--panel-open': props.isPanelOpen,
            'c-naa-ai-chat-base--panel-closed': !props.isPanelOpen,
          })}
        >
          {state.isMobile && appState.isSidebarOpen && (
            <button
              type="button"
              className="c-naa-ai-chat-base__backdrop u-els-debuttonize"
              onClick={() => {
                setAppState((prevState) => ({
                  ...prevState,
                  isSidebarOpen: false,
                }));
              }}
              aria-label="Close sidebar"
            />
          )}
          {!hideNav && (
            <div
              className={cx("c-naa-ai-chat-base__sidebar", {
                'c-naa-ai-chat-base__sidebar--closed': !appState.isSidebarOpen,
                'c-naa-ai-chat-base__sidebar--open': appState.isSidebarOpen
              })}
              style={{ width: state.sidebarWidth }}
            >
              <div className="c-naa-ai-chat-base__sidebar-top">

                {
                  !appState.isSidebarOpen && (
                    <>
                      <div className="c-naa-ai-chat-base__sidebar-section">
                        <FlexLayout>
                          <FlexItem>
                            {toggleSidebarButton}
                          </FlexItem>
                        </FlexLayout>
                      </div>

                      <nav aria-label="Main navigation">
                        <ul>
                          {
                            getSidebarItems().map((item) => {
                              const config = SidebarItemConfig[item];
                              if (!config) {
                                return null;
                              }
                              const isActive = history.location.pathname === config.route;
                              return (
                                <li
                                  key={item}
                                  className={
                                    cx(
                                      "c-naa-ai-chat-base__sidebar-item",
                                      `c-naa-ai-chat-base__sidebar-item--${item.toLowerCase()}`,
                                      {
                                        'c-naa-ai-chat-base__sidebar-item--active': isActive
                                      }
                                    )
                                  }
                                >
                                  <FlexLayout>
                                    <FlexItem>
                                      <button
                                        type="button"
                                        title={config.text}
                                        id={`${item.toLowerCase()}-btn`}
                                        className="u-els-debuttonize"
                                        aria-current={isActive ? "page" : undefined}
                                        onClick={() => handleSidebarItemClick(item)}
                                      >
                                        <ELSIcon
                                          name={config.iconName}
                                          prefix={config.iconPrefix}
                                          size="1x1o4"
                                        />
                                        <span className="u-els-hide-visually">
                                          {config.text}
                                        </span>
                                      </button>
                                    </FlexItem>
                                  </FlexLayout>
                                </li>
                              )
                            })
                          }
                        </ul>
                      </nav>
                    </>

                  )
                }


                {
                  appState.isSidebarOpen && (
                    <>
                      <div className="c-naa-ai-chat-base__sidebar-section">

                        <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                          <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                            <div>
                              {!showHeaderLogoAsLink && (
                                <h1 id="header-logo" className="c-naa-ai-chat-base__logo u-els-font-size-h4">{getBrandName(appState)}</h1>
                              )}
                              {showHeaderLogoAsLink && (
                                <button
                                  className="u-els-debuttonize u-els-anchorize u-els-display-block"
                                  type="button"
                                  onClick={() => {
                                    // Close sidebar on mobile after clicking
                                    if (state.isMobile) {
                                      setAppState((prevState) => ({
                                        ...prevState,
                                        isSidebarOpen: false,
                                      }));
                                    }

                                    if (window.parent) {
                                      window.parent.postMessage({ type: IframeEvent.SHERPATH_AI_LINK_CLICK }, '*');
                                    }
                                  }}
                                >
                                  <h1 id="header-logo" className="u-els-font-size-h4">{getBrandName(appState)}</h1>
                                </button>
                              )}
                            </div>
                          </FlexItem>
                          <FlexItem>
                            {toggleSidebarButton}
                          </FlexItem>
                        </FlexLayout>


                        {
                          enableReturn && (
                            <div className="u-els-margin-top-1o2">
                              <button
                                className="u-els-debuttonize u-els-anchorize u-els-display-block"
                                type="button"
                                onClick={handleReturnClick}
                              >
                                Back to {brand === Brand.OSMOSIS_AI ? 'Osmosis' : 'Sherpath'}
                              </button>
                            </div>
                          )
                        }
                      </div>

                      <div className="c-naa-ai-chat-base__sidebar-section">
                        <nav aria-label="Main navigation">
                          <ul>
                            {
                              getSidebarItems().map((item) => {
                                const config = SidebarItemConfig[item];
                                if (!config) {
                                  return null;
                                }
                                const isActive = history.location.pathname === config.route;
                                return (
                                  <li
                                    key={item}
                                    className={
                                      cx(
                                        "c-naa-ai-chat-base__sidebar-item",
                                        `c-naa-ai-chat-base__sidebar-item--${item.toLowerCase()}`,
                                        {
                                          'c-naa-ai-chat-base__sidebar-item--active': isActive
                                        }
                                      )
                                    }
                                  >
                                    <button
                                      type="button"
                                      id={`${item.toLowerCase()}-btn`}
                                      className="u-els-debuttonize"
                                      aria-current={isActive ? "page" : undefined}
                                      onClick={() => handleSidebarItemClick(item)}
                                    >
                                      <IconWithText
                                        iconName={config.iconName}
                                        iconPrefix={config.iconPrefix}
                                        iconSize="1x1o4"
                                      >
                                        {config.text}
                                      </IconWithText>
                                    </button>
                                  </li>
                                )
                              })
                            }
                          </ul>
                        </nav>

                      </div>

                      <div className="c-naa-ai-chat-base__sidebar-section">
                        <h2 className="u-els-margin-bottom u-els-color-n8 u-els-font-size-h4">Recent chats</h2>
                        <ul aria-label="Recent conversations">
                          {
                            appState.chats && getSortedChats(appState.chats).slice(0, 5).map((chat) => {
                              const isActive = appState.activeChatId === chat.id && history.location.pathname === RoutePath.AI_CHAT;
                              return (
                                <li
                                  key={chat.id}
                                  className={cx('c-naa-ai-chat-base__sidebar-item', {
                                    'c-naa-ai-chat-base__sidebar-item--active': isActive
                                  })}
                                >
                                  <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
                                    <FlexItem
                                      classes={['c-naa-ai-chat-base__sidebar-item-btn']}
                                      modifiers={[FlexLayoutModifier.GROW]}>
                                      <button
                                        type="button"
                                        className="u-els-debuttonize u-els-width-1o1"
                                        aria-current={isActive ? "page" : undefined}
                                        title={chat.title}
                                        onClick={() => {
                                          // Close sidebar on mobile after clicking
                                          if (state.isMobile) {
                                            setAppState((prevState) => ({
                                              ...prevState,
                                              isSidebarOpen: false,
                                            }));
                                          }

                                          handleChatClickAction({
                                            appState,
                                            appStateRef,
                                            setAppState,
                                            chat,
                                            sendClickEvent: true,
                                            checkLoading: true,
                                            toastService: props.toastService
                                          });
                                          history.push(preserveAdminOverrideParam(RoutePath.AI_CHAT, history.location.search))
                                        }}
                                      >
                                        {chat.title}
                                      </button>
                                    </FlexItem>
                                    <FlexItem>
                                      <ChatActionMenu chat={chat} idPrefix="sidebar" />
                                    </FlexItem>
                                  </FlexLayout>

                                </li>
                              )
                            })
                          }
                        </ul>
                      </div>
                    </>
                  )
                }


              </div>
              <div className="c-naa-ai-chat-base__sidebar-bottom">
                <div className="c-naa-ai-chat-base__sidebar-section">
                  <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
                    <FlexItem
                      modifiers={[FlexLayoutModifier.GROW]}
                      isRender={appState.isSidebarOpen}
                    >
                      <div>
                        <IsRender isRender={!!appState.eolsUser && brand !== Brand.NORA}>
                          <div className="c-naa-ai-chat-base__sidebar-username">
                            {getUserName(appState).fullName}
                          </div>
                        </IsRender>
                        <IsRender isRender={isInstructor(appState.userRole)}>
                          <div className="u-els-color-n8">
                            {ChatUserRoleDisplayMap[appState.chatUserRole]}
                          </div>
                        </IsRender>
                      </div>
                    </FlexItem>
                    <FlexItem>
                      <ELSTooltip
                        id="settings-menu-tooltip"
                        showClose={false}
                        hide={state.hideTooltip}
                        triggerAriaLabel="Settings"
                        dialogAriaLabel="Settings menu"
                        onOpenToolTip={() => {
                          setState((prevState) => ({
                            ...prevState,
                            hideTooltip: false,
                          }));
                        }}
                        onCloseToolTip={() => {
                          setState((prevState) => ({
                            ...prevState,
                            hideTooltip: true,
                          }));
                        }}
                        content={(
                          <div>
                            <div>
                              <button
                                type="button"
                                title="settings"
                                className="u-els-anchorize"
                                onClick={() => handleSidebarItemClick(SidebarItem.HOW_IT_WORKS)}
                              >
                                <IconWithText
                                  iconName={SidebarItemConfig[SidebarItem.HOW_IT_WORKS].iconName}
                                  iconPrefix={SidebarItemConfig[SidebarItem.HOW_IT_WORKS].iconPrefix}
                                  iconSize="1x1o4"
                                >
                                  {SidebarItemConfig[SidebarItem.HOW_IT_WORKS].text}
                                </IconWithText>
                              </button>
                            </div>

                            <IsRender isRender={isInstructor(appState.userRole)}>
                              {getChatUserRoleTooltipContent()}
                            </IsRender>
                          </div>
                        )}
                      >
                        <span>
                          <ELSIcon
                            size="1x1o4"
                            prefix="gizmo"
                            name="settings"
                            customClass="u-els-display-block"
                          />
                        </span>
                      </ELSTooltip>
                    </FlexItem>
                  </FlexLayout>
                </div>
              </div>

            </div>
          )}
          <div className=" c-naa-ai-chat-base__page">
            <div className=" c-naa-ai-chat-base__main">
              {state.isMobile && (
                <div className="c-naa-ai-chat-base__mobile-toggle">
                  <button
                    className="c-naa-ai-chat-base__mobile-toggle-btn u-els-debuttonize"
                    title={appState.isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                    type="button"
                    onClick={() => {
                      setAppState((prevState) => {
                        return {
                          ...prevState,
                          isSidebarOpen: !prevState.isSidebarOpen,
                        }
                      })
                    }}
                  >
                    <PanelLeftIcon size="1x1o2" />
                    <span className="u-els-hide-visually">{appState.isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}</span>
                  </button>
                </div>
              )}
              {props.children}
            </div>
            {
              props.panelContent && (
                <div
                  className={cx("c-naa-ai-chat-base__panel", {
                    'c-naa-ai-chat-base__panel--closed': !props.isPanelOpen,
                    'c-naa-ai-chat-base__panel--open': props.isPanelOpen
                  })}
                >
                  {props.isPanelOpen ? (
                    <FocusTrap focusTrapOptions={{
                      preventScroll: true,
                      clickOutsideDeactivates: true
                    }}>
                      {props.panelContent || <div />}
                    </FocusTrap>
                  ) : (
                    props.panelContent
                  )}
                </div>
              )
            }
          </div>
        </div>
      </div>
    </>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  ELSWithModalService,
  withPageLoader,
);

const AiChatBaseTemplate = enhance(AiChatBaseTemplateComponent as React.FC<AiChatBaseTemplateProps>) as React.ComponentType<AiChatBaseTemplatePropsOnly>;

export default AiChatBaseTemplate;
